/* eslint-disable no-console */
import JsBarcode from 'jsbarcode';

/**
 * Professional Barcode Generator
 * Replaces simulation with real barcode generation using JsBarcode
 * kware: إضافة مولد باركود احترافي لاستبدال المحاكاة الموجودة
 */
class RealBarcodeGenerator {
  constructor() {
    this.supportedFormats = [
      'CODE128', 'CODE39', 'EAN13', 'EAN8', 'UPC-A', 'UPC-E',
      'ITF', 'MSI', 'pharmacode', 'codabar'
    ];
    this.defaultSettings = {
      width: 2,
      height: 100,
      displayValue: true,
      fontSize: 14,
      textPosition: 'bottom',
      background: '#ffffff',
      lineColor: '#000000',
      margin: 10
    };
  }

  /**
   * Generate real barcode element for display
   */
  generateBarcodeElement(barcode, format, options = {}) {
    if (!barcode || barcode.trim() === '') {
      return this.generateErrorBarcode('N/A');
    }

    const canvas = document.createElement('canvas');
    const settings = { ...this.defaultSettings, ...options };

    // Validate format
    const validFormat = this.validateFormat(format);

    try {
      JsBarcode(canvas, barcode, {
        format: validFormat,
        width: settings.width,
        height: settings.height,
        displayValue: settings.displayValue,
        fontSize: settings.fontSize,
        textPosition: settings.textPosition,
        background: settings.background,
        lineColor: settings.lineColor,
        margin: settings.margin,
        textAlign: 'center',
        textMargin: 2,
        font: 'monospace'
      });

      return canvas;
    } catch (error) {
      console.error('Barcode generation failed:', error);
      return this.generateErrorBarcode(barcode);
    }
  }

  /**
   * Generate high-quality SVG barcode for printing
   */
  generatePrintableBarcode(barcode, format, options = {}) {
    if (!barcode || barcode.trim() === '') {
      return null;
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const settings = {
      ...this.defaultSettings,
      width: 3, // Higher width for printing
      height: 120, // Higher height for printing
      fontSize: 16,
      ...options
    };

    const validFormat = this.validateFormat(format);

    try {
      JsBarcode(svg, barcode, {
        format: validFormat,
        width: settings.width,
        height: settings.height,
        displayValue: settings.displayValue,
        fontSize: settings.fontSize,
        background: settings.background,
        lineColor: settings.lineColor,
        textAlign: 'center',
        textMargin: 2,
        font: 'monospace'
      });

      return svg.outerHTML;
    } catch (error) {
      console.error('Printable barcode generation failed:', error);
      return null;
    }
  }

  /**
   * Generate barcode as data URL for embedding
   */
  generateBarcodeDataURL(barcode, format, options = {}) {
    const canvas = this.generateBarcodeElement(barcode, format, options);
    if (canvas) {
      try {
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.error('Data URL generation failed:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Validate and normalize barcode format
   */
  validateFormat(format) {
    if (!format || !this.supportedFormats.includes(format.toUpperCase())) {
      return 'CODE128'; // Default fallback
    }
    return format.toUpperCase();
  }

  /**
   * Generate error barcode when main generation fails
   */
  generateErrorBarcode(barcode) {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 60;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f8f8f8';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#ccc';
    ctx.strokeRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#666';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Barcode Error', canvas.width / 2, canvas.height / 2 - 5);
    ctx.fillText(barcode || 'N/A', canvas.width / 2, canvas.height / 2 + 10);

    return canvas;
  }

  /**
   * Get barcode dimensions for layout calculation
   */
  getBarcodeSize(barcode, format, options = {}) {
    const settings = { ...this.defaultSettings, ...options };
    const estimatedWidth = barcode.length * settings.width + (settings.margin * 2);
    const totalHeight = settings.height + (settings.displayValue ? settings.fontSize + 10 : 0) + (settings.margin * 2);

    return {
      width: estimatedWidth,
      height: totalHeight
    };
  }

  /**
   * Validate barcode content for specific formats
   */
  validateBarcodeContent(barcode, format) {
    const validFormat = this.validateFormat(format);

    switch (validFormat) {
      case 'EAN13':
        return /^\d{13}$/.test(barcode);
      case 'EAN8':
        return /^\d{8}$/.test(barcode);
      case 'UPC-A':
        return /^\d{12}$/.test(barcode);
      case 'UPC-E':
        return /^\d{8}$/.test(barcode);
      case 'CODE39':
        return /^[0-9A-Z\-. $\/+%]+$/.test(barcode);
      case 'CODE128':
        return barcode.length > 0; // CODE128 accepts most characters
      default:
        return barcode.length > 0;
    }
  }

  /**
   * Get format suggestions for a barcode
   */
  suggestFormat(barcode) {
    if (!barcode) return ['CODE128'];

    const suggestions = [];

    // Check numeric patterns
    if (/^\d+$/.test(barcode)) {
      if (barcode.length === 13) suggestions.push('EAN13');
      if (barcode.length === 12) suggestions.push('UPC-A');
      if (barcode.length === 8) suggestions.push('EAN8', 'UPC-E');
      suggestions.push('CODE128', 'CODE39');
    } else if (/^[0-9A-Z\-. $\/+%]+$/.test(barcode)) {
      suggestions.push('CODE39', 'CODE128');
    } else {
      suggestions.push('CODE128');
    }

    return suggestions;
  }

  /**
   * Get supported formats list
   */
  getSupportedFormats() {
    return this.supportedFormats.map(format => ({
      value: format,
      label: format,
      description: this.getFormatDescription(format)
    }));
  }

  /**
   * Get format description
   */
  getFormatDescription(format) {
    const descriptions = {
      'CODE128': 'Universal format, supports all ASCII characters',
      'CODE39': 'Alphanumeric format, widely supported',
      'EAN13': 'European Article Number (13 digits)',
      'EAN8': 'European Article Number (8 digits)',
      'UPC-A': 'Universal Product Code (12 digits)',
      'UPC-E': 'Universal Product Code (8 digits)',
      'ITF': 'Interleaved 2 of 5, numeric only',
      'MSI': 'Modified Plessey, numeric only',
      'pharmacode': 'Pharmaceutical Binary Code',
      'codabar': 'Codabar format, for libraries and blood banks'
    };

    return descriptions[format] || 'Standard barcode format';
  }
}

export default RealBarcodeGenerator;
